import { useCallback, useRef, useState } from 'react';

type CaptchaSubmitHandler<TValues extends object> = (
  values: TValues & { captchaToken: string }
) => unknown;

export const useInvisibleCaptchaSubmit = <TValues extends object>(
  onSubmit?: CaptchaSubmitHandler<TValues>,
  onError?: () => void
) => {
  const valuesRef = useRef<TValues | null>(null);
  const [visible, setVisible] = useState(false);
  const [isSubmitting, setSubmitting] = useState(false);
  const [captchaKey, setCaptchaKey] = useState(0);

  const resetCaptcha = useCallback(() => {
    setCaptchaKey((key) => key + 1);
  }, []);

  const handleSubmit = useCallback((values: TValues) => {
    valuesRef.current = values;
    setVisible(true);
  }, []);

  const handleError = useCallback(() => {
    valuesRef.current = null;
    setSubmitting(false);
    setVisible(false);
    resetCaptcha();
    onError?.();
  }, [onError, resetCaptcha]);

  const handleChallengeHidden = useCallback(() => {
    setVisible(false);
  }, []);

  const handleSuccess = useCallback(
    async (captchaToken: string) => {
      const values = valuesRef.current;

      if (!values || !captchaToken) {
        handleError();
        return;
      }

      setSubmitting(true);

      try {
        await onSubmit?.({ ...values, captchaToken });
      } finally {
        setSubmitting(false);
        valuesRef.current = null;
        setVisible(false);
        resetCaptcha();
      }
    },
    [handleError, onSubmit, resetCaptcha]
  );

  return {
    captchaKey,
    captchaProps: {
      onChallengeHidden: handleChallengeHidden,
      onJavascriptError: handleError,
      onNetworkError: handleError,
      onSuccess: handleSuccess,
      onTokenExpired: handleError,
      visible,
    },
    handleSubmit,
    isSubmitting,
  };
};
